/**
 * FIND ANYTHING IN THE ROOM: a table, or an item by its words. Opened
 * from the location bar, or with "/" or ⌘K from anywhere on the page.
 *
 * Tables are already on the page, so they are matched here as you
 * type. Items are searched on the server (title and body), so the whole board
 * is searched, not just what the feed has loaded.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { BoardroomItem } from '../../types';

export interface FindTarget {
  key: string;
  label: string;
  /** A short word after the label: "table", "company". */
  what: string;
  onPick: () => void;
}

/** True when a key press belongs to something being typed in. */
function typing(e: KeyboardEvent): boolean {
  const el = e.target as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
}

/** Local matches: every word typed must appear in the label. Pure. */
export function matchTargets(targets: FindTarget[], q: string): FindTarget[] {
  const words = q.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return targets;
  return targets.filter(t => {
    const label = t.label.toLowerCase();
    return words.every(w => label.includes(w));
  });
}

export default function FindBox({
  targets,
  searchItems,
  onItem,
}: {
  /** The tables, matched on the page. */
  targets: FindTarget[];
  searchItems: (q: string) => Promise<BoardroomItem[]>;
  onItem: (item: BoardroomItem) => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [items, setItems] = useState<BoardroomItem[] | null>(null);
  const [active, setActive] = useState(0);
  const box = useRef<HTMLDivElement>(null);

  // "/" or ⌘K opens it from anywhere that is not a text box.
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      const cmdK = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k';
      if (cmdK || (e.key === '/' && !typing(e) && !e.metaKey && !e.ctrlKey && !e.altKey)) {
        e.preventDefault();
        setOpen(true);
      }
    };
    document.addEventListener('keydown', key);
    return () => document.removeEventListener('keydown', key);
  }, []);

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);

  // Items wait for two letters and a pause in typing.
  useEffect(() => {
    const text = q.trim();
    if (!open || text.length < 2) {
      setItems(null);
      return;
    }
    let live = true;
    const t = window.setTimeout(() => {
      searchItems(text)
        .then(r => live && setItems(r))
        .catch(() => live && setItems([]));
    }, 200);
    return () => {
      live = false;
      window.clearTimeout(t);
    };
  }, [open, q, searchItems]);

  const local = useMemo(() => matchTargets(targets, q), [targets, q]);
  const rows: FindTarget[] = useMemo(
    () => [
      ...local,
      ...(items ?? []).map(it => ({
        key: `item:${it.id}`,
        label: it.title,
        what: it.kind.toLowerCase().replace(/_/g, ' '),
        onPick: () => onItem(it),
      })),
    ],
    [local, items, onItem],
  );
  useEffect(() => setActive(0), [q]);

  const close = () => {
    setOpen(false);
    setQ('');
  };
  const pick = (r: FindTarget) => {
    close();
    r.onPick();
  };

  return (
    <div ref={box} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        title="Find a table or item (press / or ⌘K)"
        className="flex items-center gap-2 rounded-md border border-gray-200 px-2 py-0.5 text-xs text-gray-500 hover:bg-gray-100 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700"
        data-testid="find-open"
      >
        Find…
        <kbd className="rounded border border-gray-200 px-1 text-[11px] text-gray-400 dark:border-gray-600">/</kbd>
      </button>
      {open && (
        <div className="absolute right-0 top-full z-30 mt-1 w-96 rounded-lg border border-gray-200 bg-white p-2 shadow-lg dark:border-gray-700 dark:bg-gray-800" data-testid="find-box">
          <input
            autoFocus
            value={q}
            onChange={e => setQ(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Escape') close();
              else if (e.key === 'ArrowDown') {
                e.preventDefault();
                setActive(a => Math.min(a + 1, rows.length - 1));
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setActive(a => Math.max(a - 1, 0));
              } else if (e.key === 'Enter' && rows[active]) {
                e.preventDefault();
                pick(rows[active]);
              }
            }}
            placeholder="A table, or words from an item…"
            className="mb-1 w-full rounded border border-gray-200 bg-white px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-900"
            data-testid="find-input"
          />
          <ul className="max-h-80 overflow-y-auto text-xs" role="listbox">
            {rows.map((r, i) => (
              <li key={r.key}>
                <button
                  type="button"
                  role="option"
                  aria-selected={i === active}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => pick(r)}
                  className={`flex w-full items-baseline gap-2 rounded px-2 py-1 text-left ${
                    i === active ? 'bg-indigo-50 dark:bg-indigo-900/30' : ''
                  }`}
                  data-testid="find-row"
                >
                  <span className="min-w-0 flex-1 truncate text-gray-800 dark:text-gray-100">{r.label}</span>
                  <span className="shrink-0 text-[11px] text-gray-400">{r.what}</span>
                </button>
              </li>
            ))}
            {q.trim().length >= 2 && items === null && <li className="px-2 py-1 text-gray-400">Searching items…</li>}
            {rows.length === 0 && items !== null && <li className="px-2 py-1 text-gray-400">Nothing matches.</li>}
          </ul>
        </div>
      )}
    </div>
  );
}
